/**
 * API Endpoints Constants
 * Centralized endpoint management for QPS Laravel API
 */

export const Endpoints = {
  // Authentication
  LOGIN: '/login',
  LOGOUT: '/logout',
  PROFILE: '/profile',
  REGISTER: '/register',
  // Note: Laravel Sanctum uses long-lived tokens, no refresh endpoint needed

  // User Management (HTTP method differentiation handled by client)
  USERS_LIST: '/users',
  USER_DETAIL: '/users/{id}',
  USER_UPDATE: '/users/{id}',
  USER_DELETE: '/users/{id}',

  // Debug & Testing
  TEST_PERMISSION: '/test-permission',

  // Dropdown Data
  DROPDOWN_ROLES: '/dropdowns/roles',
  DROPDOWN_PROPERTIES: '/dropdowns/properties',
  DROPDOWN_PROPERTY_TYPES: '/dropdowns/property-types',
  DROPDOWN_DEPARTMENTS: '/dropdowns/departments',

  // SRS Hierarchy Dropdowns
  DROPDOWN_DEPARTMENTS_BY_PROPERTY_TYPE:
    '/dropdowns/departments/property-type/{propertyTypeId}',
  DROPDOWN_TOUCHPOINTS_BY_DEPARTMENT:
    '/dropdowns/touchpoints/department/{departmentId}',
  DROPDOWN_TOUCHPOINTS_BY_PROPERTY_TYPE:
    '/dropdowns/touchpoints/property-type/{propertyTypeId}',
  DROPDOWN_TITLES_BY_PROPERTY_TYPE:
    '/dropdowns/titles/property-type/{propertyTypeId}',
  DROPDOWN_SUB_TOUCHPOINTS_BY_PROPERTY_TYPE:
    '/dropdowns/sub-touchpoints/property-type/{propertyTypeId}',

  // Future Endpoints (for reference)
  // Properties
  PROPERTIES_LIST: '/properties',
  PROPERTY_DETAIL: '/properties/{id}',
  PROPERTY_CREATE: '/properties',
  PROPERTY_UPDATE: '/properties/{id}',
  PROPERTY_DELETE: '/properties/{id}',

  // Property Types
  PROPERTY_TYPES_LIST: '/property-types',
  PROPERTY_TYPE_CREATE: '/property-types',
  PROPERTY_TYPE_UPDATE: '/property-types/{id}',
  PROPERTY_TYPE_DELETE: '/property-types/{id}',

  // Assessments
  ASSESSMENTS_LIST: '/assessments',
  ASSESSMENT_DETAIL: '/assessments/{id}',
  ASSESSMENT_CREATE: '/assessments',
  ASSESSMENT_UPDATE: '/assessments/{id}',
  ASSESSMENT_DELETE: '/assessments/{id}',

  // Templates
  TEMPLATES_LIST: '/templates',
  TEMPLATE_DETAIL: '/templates/{id}',
  TEMPLATE_CREATE: '/templates',
  TEMPLATE_UPDATE: '/templates/{id}',
  TEMPLATE_DELETE: '/templates/{id}',

  // Template Management - Standards
  STANDARDS_LIST: '/property-types/{propertyTypeId}/standards',
  STANDARD_DETAIL: '/standards/{id}',
  STANDARD_CREATE: '/standards',
  STANDARD_UPDATE: '/standards/{id}',
  STANDARD_DELETE: '/standards/{id}',
  STANDARDS_EXPORT: '/property-types/{propertyTypeId}/standards/export',

  // Template Management - Departments
  DEPARTMENTS_LIST: '/property-types/{propertyTypeId}/departments',
  DEPARTMENT_DETAIL: '/departments/{id}',
  DEPARTMENT_CREATE: '/departments',
  DEPARTMENT_UPDATE: '/departments/{id}',
  DEPARTMENT_DELETE: '/departments/{id}',

  // Template Management - Touchpoints
  TOUCHPOINTS_LIST: '/property-types/{propertyTypeId}/touchpoints',
  TOUCHPOINT_DETAIL: '/touchpoints/{id}',
  TOUCHPOINT_CREATE: '/touchpoints',
  TOUCHPOINT_UPDATE: '/touchpoints/{id}',
  TOUCHPOINT_DELETE: '/touchpoints/{id}',

  // Template Management - SubTouchpoints
  SUB_TOUCHPOINTS_LIST: '/property-types/{propertyTypeId}/sub-touchpoints',
  SUB_TOUCHPOINT_DETAIL: '/sub-touchpoints/{id}',
  SUB_TOUCHPOINT_CREATE: '/sub-touchpoints',
  SUB_TOUCHPOINT_UPDATE: '/sub-touchpoints/{id}',
  SUB_TOUCHPOINT_DELETE: '/sub-touchpoints/{id}',

  // Template Management - Titles
  TITLES_LIST: '/property-types/{propertyTypeId}/titles',
  TITLE_DETAIL: '/titles/{id}',
  TITLE_CREATE: '/titles',
  TITLE_UPDATE: '/titles/{id}',
  TITLE_DELETE: '/titles/{id}',

  // Tasks
  TASKS_LIST: '/tasks',
  TASK_DETAIL: '/tasks/{id}',
  TASK_CREATE: '/tasks',
  TASK_UPDATE: '/tasks/{id}',
  TASK_DELETE: '/tasks/{id}',

  // Reports
  REPORTS_LIST: '/reports',
  REPORT_DETAIL: '/reports/{id}',
  REPORT_GENERATE: '/reports/generate',

  // Media/File Upload
  MEDIA_UPLOAD: '/media/upload',
  MEDIA_DELETE: '/media/{id}',

  // Audit Logs
  AUDIT_LOGS: '/audit-logs',
} as const;

/**
 * Helper function to replace path parameters in endpoints
 * @param endpoint - The endpoint with placeholders
 * @param params - Object containing parameter values
 * @returns String with replaced parameters
 *
 * @example
 * replaceParams(Endpoints.USER_DETAIL, { id: '123' })
 * // Returns: '/users/123'
 */
export function replaceParams(
  endpoint: string,
  params: Record<string, string | number>
): string {
  let result = endpoint;

  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`{${key}}`, String(value));
  });

  return result;
}

/**
 * Predefined endpoint replacements for common use cases
 */
export const EndpointHelpers = {
  // User endpoints
  getUserDetail: (id: string | number) =>
    replaceParams(Endpoints.USER_DETAIL, { id }),

  updateUser: (id: string | number) =>
    replaceParams(Endpoints.USER_UPDATE, { id }),

  deleteUser: (id: string | number) =>
    replaceParams(Endpoints.USER_DELETE, { id }),

  // Property endpoints
  getPropertyDetail: (id: string | number) =>
    replaceParams(Endpoints.PROPERTY_DETAIL, { id }),

  updateProperty: (id: string | number) =>
    replaceParams(Endpoints.PROPERTY_UPDATE, { id }),

  deleteProperty: (id: string | number) =>
    replaceParams(Endpoints.PROPERTY_DELETE, { id }),

  // Property Type endpoints
  updatePropertyType: (id: string | number) =>
    replaceParams(Endpoints.PROPERTY_TYPE_UPDATE, { id }),

  deletePropertyType: (id: string | number) =>
    replaceParams(Endpoints.PROPERTY_TYPE_DELETE, { id }),

  // Assessment endpoints
  getAssessmentDetail: (id: string | number) =>
    replaceParams(Endpoints.ASSESSMENT_DETAIL, { id }),

  updateAssessment: (id: string | number) =>
    replaceParams(Endpoints.ASSESSMENT_UPDATE, { id }),

  deleteAssessment: (id: string | number) =>
    replaceParams(Endpoints.ASSESSMENT_DELETE, { id }),

  // Template endpoints
  getTemplateDetail: (id: string | number) =>
    replaceParams(Endpoints.TEMPLATE_DETAIL, { id }),

  updateTemplate: (id: string | number) =>
    replaceParams(Endpoints.TEMPLATE_UPDATE, { id }),

  deleteTemplate: (id: string | number) =>
    replaceParams(Endpoints.TEMPLATE_DELETE, { id }),

  // Standards endpoints
  getStandardsList: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.STANDARDS_LIST, { propertyTypeId }),

  getStandardDetail: (id: string | number) =>
    replaceParams(Endpoints.STANDARD_DETAIL, { id }),

  updateStandard: (id: string | number) =>
    replaceParams(Endpoints.STANDARD_UPDATE, { id }),

  deleteStandard: (id: string | number) =>
    replaceParams(Endpoints.STANDARD_DELETE, { id }),

  exportStandards: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.STANDARDS_EXPORT, { propertyTypeId }),

  // Departments endpoints
  getDepartmentsList: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.DEPARTMENTS_LIST, { propertyTypeId }),

  getDepartmentDetail: (id: string | number) =>
    replaceParams(Endpoints.DEPARTMENT_DETAIL, { id }),

  updateDepartment: (id: string | number) =>
    replaceParams(Endpoints.DEPARTMENT_UPDATE, { id }),

  deleteDepartment: (id: string | number) =>
    replaceParams(Endpoints.DEPARTMENT_DELETE, { id }),

  // Touchpoints endpoints
  getTouchpointsList: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.TOUCHPOINTS_LIST, { propertyTypeId }),

  getTouchpointDetail: (id: string | number) =>
    replaceParams(Endpoints.TOUCHPOINT_DETAIL, { id }),

  updateTouchpoint: (id: string | number) =>
    replaceParams(Endpoints.TOUCHPOINT_UPDATE, { id }),

  deleteTouchpoint: (id: string | number) =>
    replaceParams(Endpoints.TOUCHPOINT_DELETE, { id }),

  // SubTouchpoints endpoints
  getSubTouchpointsList: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.SUB_TOUCHPOINTS_LIST, { propertyTypeId }),

  getSubTouchpointDetail: (id: string | number) =>
    replaceParams(Endpoints.SUB_TOUCHPOINT_DETAIL, { id }),

  updateSubTouchpoint: (id: string | number) =>
    replaceParams(Endpoints.SUB_TOUCHPOINT_UPDATE, { id }),

  deleteSubTouchpoint: (id: string | number) =>
    replaceParams(Endpoints.SUB_TOUCHPOINT_DELETE, { id }),

  // Titles endpoints
  getTitlesList: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.TITLES_LIST, { propertyTypeId }),

  getTitleDetail: (id: string | number) =>
    replaceParams(Endpoints.TITLE_DETAIL, { id }),

  updateTitle: (id: string | number) =>
    replaceParams(Endpoints.TITLE_UPDATE, { id }),

  deleteTitle: (id: string | number) =>
    replaceParams(Endpoints.TITLE_DELETE, { id }),

  // Task endpoints
  getTaskDetail: (id: string | number) =>
    replaceParams(Endpoints.TASK_DETAIL, { id }),

  updateTask: (id: string | number) =>
    replaceParams(Endpoints.TASK_UPDATE, { id }),

  deleteTask: (id: string | number) =>
    replaceParams(Endpoints.TASK_DELETE, { id }),

  // Report endpoints
  getReportDetail: (id: string | number) =>
    replaceParams(Endpoints.REPORT_DETAIL, { id }),

  // Media endpoints
  deleteMedia: (id: string | number) =>
    replaceParams(Endpoints.MEDIA_DELETE, { id }),

  // SRS Hierarchy endpoints
  getDepartmentsByPropertyType: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.DROPDOWN_DEPARTMENTS_BY_PROPERTY_TYPE, {
      propertyTypeId,
    }),

  getTouchpointsByDepartment: (departmentId: string | number) =>
    replaceParams(Endpoints.DROPDOWN_TOUCHPOINTS_BY_DEPARTMENT, {
      departmentId,
    }),

  getTouchpointsByPropertyType: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.DROPDOWN_TOUCHPOINTS_BY_PROPERTY_TYPE, {
      propertyTypeId,
    }),

  getTitlesByPropertyType: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.DROPDOWN_TITLES_BY_PROPERTY_TYPE, {
      propertyTypeId,
    }),

  getSubTouchpointsByPropertyType: (propertyTypeId: string | number) =>
    replaceParams(Endpoints.DROPDOWN_SUB_TOUCHPOINTS_BY_PROPERTY_TYPE, {
      propertyTypeId,
    }),
} as const;
